/**
 * One-shot sound effects synthesised on demand via Web Audio.
 * Lazy AudioContext (created on first use, after a user gesture).
 */
class SfxImpl {
  private ctx?: AudioContext;
  private master?: GainNode;

  private ensureCtx(): AudioContext | null {
    if (typeof window === 'undefined' || !window.AudioContext) return null;
    if (!this.ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = 0.55;
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  /** Punch: low sine thud + short bandpassed noise burst for "smack". */
  punch() {
    const ctx = this.ensureCtx();
    if (!ctx || !this.master) return;
    const t0 = ctx.currentTime;

    // Low body — sine sweep down
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(140, t0);
    o.frequency.exponentialRampToValueAtTime(45, t0 + 0.10);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, t0);
    og.gain.exponentialRampToValueAtTime(0.55, t0 + 0.005);
    og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
    o.connect(og).connect(this.master);
    o.start(t0);
    o.stop(t0 + 0.18);

    // Smack — short bandpassed noise
    const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.08, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const n = ctx.createBufferSource();
    n.buffer = noiseBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800;
    bp.Q.value = 1.0;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, t0);
    ng.gain.exponentialRampToValueAtTime(0.35, t0 + 0.004);
    ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.08);
    n.connect(bp).connect(ng).connect(this.master);
    n.start(t0);
    n.stop(t0 + 0.10);
  }
}

export const Sfx = new SfxImpl();
